import React, { createContext, ReactNode, useContext, useState } from 'react'

import { IReturnPayload } from 'main/components/common/AuthModal/ssoNavigation'
import { userDashboardPath } from 'main/routes'

export enum NAV_MODALS {
  ADD_FAV = 'ADD_FAV',
  ENROLL = 'ENROLL',
  FULL_PRICING = 'FULL_PRICING',
  MESSAGE_DIRECTOR = 'MESSAGE_DIRECTOR',
  SCHEDULE_TOUR = 'SCHEDULE_TOUR',
  SAVE_SEARCH = 'SAVE_SEARCH',
  REQUEST_TOUR = 'REQUEST_TOUR',
}

export interface INavContextOptional {
  route?: string
  modal?: NAV_MODALS
  isOnboarding?: boolean
}
export interface INavContext extends INavContextOptional {
  route: string
}
export type ExtendNavContext = (extra: INavContextOptional, defaultRoute?: string) => INavContext
export interface ISSOContext {
  ssoContext?: IReturnPayload
  setSSOContext: (obj?: IReturnPayload) => void
  navContext?: INavContext
  setNavContext: (obj?: INavContext) => void
  extendNavContext: ExtendNavContext
  persistContext: () => void
}

const SSOContext = createContext<ISSOContext>({
  ssoContext: undefined,
  setSSOContext: () => undefined,
  navContext: undefined,
  setNavContext: () => undefined,
  extendNavContext: () => ({ route: '' }),
  persistContext: () => undefined,
})

interface IPersistPayload {
  ssoContext?: IReturnPayload
  navContext?: INavContext
}
const KEY = 'sso_nav_context'
const persistContext = (ssoContext?: IReturnPayload, navContext?: INavContext) => {
  window.localStorage.setItem(
    KEY,
    JSON.stringify({
      ssoContext,
      navContext,
    }),
  )
}
export const consumeContext = (): IPersistPayload => {
  const lsVal = window.localStorage.getItem(KEY)
  const persisted = !lsVal ? {} : JSON.parse(lsVal)
  window.localStorage.removeItem(KEY)
  return persisted
}

const SSOContextProvider = ({ children }: { children: ReactNode }) => {
  const maybeSaved = consumeContext()
  const [ssoContext, setSSOContext] = useState<IReturnPayload | undefined>(maybeSaved?.ssoContext)
  const [navContext, setNavContext] = useState<INavContext | undefined>(maybeSaved?.navContext)

  const extendNavContext = (extra: INavContextOptional, defaultRoute?: string) => {
    const maybeRoute = defaultRoute ?? userDashboardPath()
    const maybeInitialContext = navContext ?? {
      route: maybeRoute,
    }
    const newContext = {
      ...maybeInitialContext,
      ...extra,
    }

    setNavContext(newContext)
    return newContext
  }

  return (
    <SSOContext.Provider
      value={{
        ssoContext,
        setSSOContext,
        navContext,
        setNavContext,
        extendNavContext,
        persistContext: () => persistContext(ssoContext, navContext),
      }}
    >
      {children}
    </SSOContext.Provider>
  )
}

const useSSOContext = () => useContext(SSOContext)

export default useSSOContext
export { SSOContextProvider }
